import React, { useEffect, useState, forwardRef } from "react";
import { Avatar as MuiAvatar, Skeleton } from "@mui/material";
import { useOptionalAvatarCache } from "../contexts/AvatarCacheContext";

interface AvatarProps {
  email?: string;
  auth0Picture?: string; // default avatar from auth0 profile
  alt?: string;
  sx?: object; // optional sx prop for custom styles
  showSkeleton?: boolean; // whether to show skeleton during loading
  variant?: "circular" | "rounded" | "square";
  children?: React.ReactNode;
}

const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      email,
      auth0Picture,
      alt,
      sx,
      showSkeleton = true,
      variant = "circular",
      children,
      ...props
    },
    ref
  ) => {
    const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(true);
    // Some isolated consumers intentionally omit the optional cache hook.
    const avatarCache = useOptionalAvatarCache?.();
    const getAvatar = avatarCache?.getAvatar;
    const getCachedAvatar = avatarCache?.getCachedAvatar;

    useEffect(() => {
      const fetchAvatar = async () => {
        if (!email) {
          setAvatarUrl(auth0Picture);
          setLoading(false);
          return;
        }
        if (!getAvatar || !getCachedAvatar) {
          setAvatarUrl(auth0Picture);
          setLoading(false);
          return;
        }


        // Check if we have cached avatar
        const cachedAvatar = getCachedAvatar(email);
        if (cachedAvatar !== undefined) {
          setAvatarUrl(cachedAvatar);
          setLoading(false);
          return;
        }

        setLoading(true);
        try {
          const url = await getAvatar(email, auth0Picture);
          setAvatarUrl(url);
        } catch (err: any) {
          console.error("Error fetching avatar", err);
          setAvatarUrl(auth0Picture);
        } finally {
          setLoading(false);
        }
      };

      fetchAvatar();
    }, [email, auth0Picture, getAvatar, getCachedAvatar]);

    if (loading && showSkeleton) {
      return (
        <Skeleton
          variant={variant as "circular" | "rounded" | "text" | "rectangular"}
          sx={{ width: 64, height: 64, borderRadius: "50%", ...sx }}
        />
      );
    }

    return (
      <MuiAvatar
        ref={ref}
        src={avatarUrl}
        alt={alt}
        sx={{
          width: 64,
          height: 64,
          borderRadius: variant === "circular" ? "50%" : "4px",
          ...sx,
        }}
        {...props}
      >{children}</MuiAvatar>
    );
  }
);

export default Avatar;
